/* sugar:assets/sugar-cart-drawer.js@2.1.13 */
/**
 * Sugar Cart Drawer
 * Main custom element for the cart drawer functionality
 */

/* ==========================================================================
   Shared cart state
   One cache for the /cart.js payload so every drawer component (toggles,
   upsells, progress, media, discount input) reads the same request instead
   of each fetching the cart on every update.
   ========================================================================== */
window.SugarCart = window.SugarCart || (function () {
  const MAX_AGE_MS = 1500;
  let cache = null;
  let previous = null;
  let cachedAt = 0;
  let inflight = null;
  let queuedFresh = null;

  function isCart(value) {
    return !!(value && Array.isArray(value.items) && typeof value.item_count === 'number');
  }

  function cartJsUrl() {
    const base = (window.routes && window.routes.cart_url) || '/cart';
    return `${base}.js`;
  }

  function keysOf(cart) {
    return cart.items.map((item) => item.key).join(',');
  }

  // Mutation responses (/cart/change, /cart/update) nest each cart-level
  // application under `discount_application`; /cart.js lists them flat.
  // Cache the flat shape so every reader sees one format.
  function normalize(cart) {
    const apps = cart.cart_level_discount_applications;
    if (Array.isArray(apps) && apps.some((entry) => entry && entry.discount_application)) {
      cart.cart_level_discount_applications = apps.map((entry) =>
        entry && entry.discount_application
          ? { ...entry.discount_application, total_allocated_amount: entry.discount_application.total_allocated_amount != null ? entry.discount_application.total_allocated_amount : entry.amount }
          : entry
      );
    }
    return cart;
  }

  function set(cart) {
    if (!isCart(cart)) return cache;
    normalize(cart);
    // Keep the last cart whose line keys differ, so a key that has just been
    // re-keyed can still be traced back to the line it named.
    if (cache && keysOf(cache) !== keysOf(cart)) previous = cache;
    cache = cart;
    cachedAt = Date.now();
    return cache;
  }

  function lineIdentity(line) {
    const plan = line.selling_plan_allocation && line.selling_plan_allocation.selling_plan;
    return JSON.stringify([String(line.variant_id), line.properties || {}, (plan && plan.id) || null]);
  }

  function findKnownLine(key) {
    for (const cart of [cache, previous]) {
      const line = cart && cart.items && cart.items.find((item) => item.key === key);
      if (line) return line;
    }
    return null;
  }

  // With a discount on the cart Shopify re-keys a line whenever its discount
  // allocation changes, so a key read from an earlier render can be rejected.
  // Find the line that key stood for in the current cart.
  async function resolveKey(key) {
    const known = findKnownLine(key);
    const fresh = await get({ fresh: true });
    if (!isCart(fresh)) return null;
    if (fresh.items.some((item) => item.key === key)) return key;
    if (known) {
      const identity = lineIdentity(known);
      const match = fresh.items.find((item) => lineIdentity(item) === identity);
      if (match) return match.key;
    }
    const variantId = String(key).split(':')[0];
    const byVariant = fresh.items.filter((item) => String(item.variant_id) === variantId);
    return byVariant.length === 1 ? byVariant[0].key : null;
  }

  function errorText(value) {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value.map(errorText).join(' ');
    if (typeof value === 'object') return Object.values(value).map(errorText).join(' ');
    return String(value);
  }

  // A stale line key comes back as 400 {"errors":"no valid id or line parameter"};
  // "Cannot find variant" is a different failure that a fresh key would not fix.
  function isInvalidLineError(result) {
    if (!result || isCart(result)) return false;
    const text = [result.errors, result.description, result.message].map(errorText).join(' ');
    if (/cannot find variant/i.test(text)) return false;
    return result.status === 'bad_request' || /id or line|line parameter|line item|not found/i.test(text);
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(async (response) => {
      const text = await response.text();
      try {
        return JSON.parse(text);
      } catch (e) {
        return { status: response.status, message: text.slice(0, 200) };
      }
    });
  }

  // POST /cart/change. Retries once with a re-resolved key when the one sent
  // has gone stale. Resolves to the cart (with `sections` when requested) or
  // to Shopify's error object.
  async function change(payload) {
    const url = (window.routes && window.routes.cart_change_url) || '/cart/change';
    let result = await post(url, payload);
    if (payload.id && isInvalidLineError(result)) {
      const key = await resolveKey(payload.id);
      if (key && key !== payload.id) result = await post(url, { ...payload, id: key });
    }
    if (isCart(result)) set(result);
    return result;
  }

  // POST /cart/update with `updates` keyed by line item key, same retry.
  async function update(payload) {
    const url = (window.routes && window.routes.cart_update_url) || '/cart/update';
    let result = await post(url, payload);
    if (payload.updates && !Array.isArray(payload.updates) && isInvalidLineError(result)) {
      const updates = {};
      for (const [key, quantity] of Object.entries(payload.updates)) {
        const resolved = await resolveKey(key);
        if (resolved) updates[resolved] = quantity;
      }
      if (Object.keys(updates).length) result = await post(url, { ...payload, updates });
    }
    if (isCart(result)) set(result);
    return result;
  }

  function invalidate() {
    cache = null;
    cachedAt = 0;
  }

  function get(options) {
    const fresh = !!(options && options.fresh);
    if (!fresh && cache && Date.now() - cachedAt < MAX_AGE_MS) {
      return Promise.resolve(cache);
    }
    if (inflight) {
      if (!fresh) return inflight;
      // A request started before the mutation may answer with the old cart:
      // run one more after it lands, shared by everyone who asked for fresh.
      if (!queuedFresh) {
        queuedFresh = inflight
          .catch(() => null)
          .then(() => {
            queuedFresh = null;
            return get({ fresh: true });
          });
      }
      return queuedFresh;
    }
    inflight = fetch(cartJsUrl(), { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => set(cart) || cart)
      .finally(() => {
        inflight = null;
      });
    return inflight;
  }

  function peek() {
    return cache;
  }

  // Extra lines other blocks want added with a buy. A provider is any element carrying
  // data-cart-extras in the same section as the form, exposing sugarExtras.collect() (lines to
  // append) and sugarExtras.commit(result) (called once they are in the cart). Product Upsells
  // queues through this instead of intercepting the form, so every buy path adds the same thing.
  function extrasProviders(scope) {
    const el = scope && scope.nodeType === 1 ? scope : null;
    let section = el ? el.closest('.shopify-section') : null;
    const find = (host) => Array.from(host.querySelectorAll('[data-cart-extras]'))
      .map((node) => node.sugarExtras)
      .filter((provider) => provider && typeof provider.collect === 'function');
    // A popup moves to <body> the first time it opens, out of its section. The section id its
    // blocks carry (a buy button's product-form, a bundle builder's root) still names the section;
    // the popup's own blocks count too. Without one there's no section to answer for: no extras.
    let dialog = null;
    if (el && !section) {
      const owner = el.closest('[data-section-id]');
      section = owner ? document.getElementById('shopify-section-' + owner.dataset.sectionId) : null;
      if (!section) return [];
      dialog = el.closest('[data-popup-dialog]');
    }
    let providers = find(section || document);
    if (dialog) providers = providers.concat(find(dialog));
    if (providers.length || !el || !section) return providers;
    // A bundle builder sells its own rows, so its button never buys on behalf of another section.
    const sectionId = section.id.replace(/^shopify-section-/, '');
    if (el.closest('[data-bundle-builder]') || document.querySelector('[data-bundle-builder][data-section-id="' + sectionId + '"]')) return providers;
    // A sticky bar buys on behalf of the main product section, so its button honours what the
    // shopper queued up there. So does a buy button in a section of its own (an advertorial
    // CTA further down the page) when the page sells exactly one product: with no product of
    // its own, it can only be buying that one.
    let mainSection = null;
    if (el.closest('sticky-custom, sticky-product')) {
      const mainForm = document.querySelector('product-form form, form[action*="/cart/add"]');
      mainSection = mainForm ? mainForm.closest('.shopify-section') : null;
    } else if (!section.querySelector('product-info')) {
      const infos = document.querySelectorAll('product-info[data-product-id]');
      mainSection = infos.length === 1 ? infos[0].closest('.shopify-section') : null;
    }
    if (mainSection && mainSection !== section) providers = find(mainSection);
    if (!providers.length) providers = sameProductProviders(el);
    return providers;
  }

  // A provider that names the product it serves (data-extras-variants: that product's variant ids)
  // also rides along with a buy button anywhere else on the page that buys the same product, so a
  // second offer card further down adds the gift picked in the first.
  function sameProductProviders(el) {
    if (!el) return [];
    const input = el.querySelector && el.querySelector('input[name="id"]');
    const variantId = String((input && input.value) || el.dataset.variantId || '');
    if (!variantId) return [];
    return Array.from(document.querySelectorAll('[data-cart-extras][data-extras-variants]'))
      .filter((node) => node.dataset.extrasVariants.split(',').includes(variantId))
      .map((node) => node.sugarExtras)
      .filter((provider) => provider && typeof provider.collect === 'function');
  }

  const extras = {
    // Two providers can promise the same line (two gift blocks naming one gift); Shopify would
    // merge those into a quantity of two, so identical lines are handed over once.
    collect(scope) {
      const seen = new Set();
      return extrasProviders(scope)
        .reduce((lines, provider) => lines.concat(provider.collect() || []), [])
        .filter((line) => {
          const key = JSON.stringify([line.id, line.selling_plan || null, line.properties || {}]);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
    },
    commit(scope, result) {
      extrasProviders(scope).forEach((provider) => {
        try {
          if (typeof provider.commit === 'function') provider.commit(result);
        } catch (e) {
          /* the provider's own problem */
        }
      });
    },
    // Providers that hold something for the buy itself (Personalization: what the shopper typed,
    // picked or uploaded) also expose validate(), filled() and decorate(items). validate() shows
    // the provider's own message and returns the element to focus when it isn't ready; the first
    // one is scrolled to and focused, and the buy stops. Given the lines being bought, a provider
    // only checks itself when they include its product.
    validate(scope, items) {
      let first = null;
      extrasProviders(scope).forEach((provider) => {
        if (typeof provider.validate !== 'function') return;
        const bad = provider.validate(items);
        if (bad && !first) first = bad;
      });
      if (first) {
        first.scrollIntoView({ block: 'center', behavior: 'smooth' });
        if (typeof first.focus === 'function') first.focus({ preventScroll: true });
        return false;
      }
      return true;
    },
    // True when a provider has something to add to the main line, so a buy that would otherwise
    // post the plain form builds its lines instead.
    decorates(scope) {
      return extrasProviders(scope).some((provider) => typeof provider.decorate === 'function' && typeof provider.filled === 'function' && provider.filled());
    },
    // Adds properties to the main product's lines and appends add-on lines nested under them.
    decorate(scope, items) {
      extrasProviders(scope).forEach((provider) => {
        if (typeof provider.decorate !== 'function') return;
        try {
          provider.decorate(items);
        } catch (e) {
          /* the provider's own problem */
        }
      });
      return items;
    },
    // The fetch body and content type for a /cart/add request: JSON, or multipart when a line
    // carries a file (Shopify stores it and keeps its URL on the line) or is nested under another
    // with parent_id (a JSON add drops a nested line without an error; tested 2026-10-14).
    request(body) {
      const lines = body.items || [];
      const hasFile = lines.some((item) => item.properties && Object.values(item.properties).some((v) => typeof Blob !== 'undefined' && v instanceof Blob));
      const nested = lines.some((item) => item.parent_id);
      if (!hasFile && !nested) return { body: JSON.stringify(body), contentType: 'application/json' };
      const form = new FormData();
      body.items.forEach((item, n) => {
        Object.keys(item).forEach((key) => {
          if (key === 'properties') {
            Object.keys(item.properties).forEach((name) => form.append(`items[${n}][properties][${name}]`, item.properties[name]));
          } else if (item[key] !== null && item[key] !== undefined) {
            form.append(`items[${n}][${key}]`, item[key]);
          }
        });
      });
      if (body.sections) form.append('sections', [].concat(body.sections).join(','));
      if (body.sections_url) form.append('sections_url', body.sections_url);
      return { body: form, contentType: null };
    },
  };

  return { get, set, invalidate, peek, isCart, change, update, resolveKey, extras };
})();

// Section ids every cart mutation asks Shopify to render alongside the cart
// so the drawer can update from the same round trip.
function sugarCartSectionIds() {
  const drawer = document.querySelector('sugar-cart-drawer');
  const sectionId = (drawer && drawer.sectionId) || 'sugar-cart-drawer';
  const ids = [sectionId, 'cart-icon-bubble'];
  // The cart page renders the same cart; when it is on screen it updates from the same round trip.
  const page = document.querySelector('sugar-cart-page[data-section-id]');
  if (page && page.dataset.sectionId && !ids.includes(page.dataset.sectionId)) ids.push(page.dataset.sectionId);
  return ids;
}

// Copy every attribute of `source` onto `target` and drop the ones it lost.
function sugarCartSyncAttributes(target, source) {
  const seen = new Set();
  for (const attr of Array.from(source.attributes)) {
    seen.add(attr.name);
    if (target.getAttribute(attr.name) !== attr.value) {
      target.setAttribute(attr.name, attr.value);
    }
  }
  for (const attr of Array.from(target.attributes)) {
    if (!seen.has(attr.name)) target.removeAttribute(attr.name);
  }
}

// Update a progress bar in place so its width transitions animate: the
// element survives, only attributes and text change. Falls back to a full
// swap when the markup shape differs (mode or goal count changed).
function sugarCartMorphProgress(target, source) {
  const modeOf = (el) => {
    if (el.querySelector('.sugar-cart-progress__milestones')) return 'milestones';
    if (el.querySelector('.sugar-cart-progress__evolving')) return 'evolving';
    return 'simple';
  };

  if (
    target.tagName !== source.tagName ||
    modeOf(target) !== modeOf(source) ||
    target.dataset.goalCount !== source.dataset.goalCount
  ) {
    target.replaceWith(source.cloneNode(true));
    return;
  }

  sugarCartSyncAttributes(target, source);

  const ATTRIBUTE_SELECTORS = [
    '.sugar-cart-progress__fill',
    '.sugar-cart-progress__milestones-fill',
    '.sugar-cart-progress__evolving-segment-fill',
    '.sugar-cart-progress__evolving-indicator',
    '.sugar-cart-progress__milestone',
    '.sugar-cart-progress__track',
    '.sugar-cart-progress__segment',
    '.sugar-cart-progress__evolving-track-segment',
  ];
  const CONTENT_SELECTORS = [
    '.sugar-cart-progress__text',
    '.sugar-cart-progress__milestone-circle',
    '.sugar-cart-progress__evolving-circle',
    '.sugar-cart-progress__label',
    '.sugar-cart-progress__milestone-label',
  ];

  const zip = (selector, fn) => {
    const targets = target.querySelectorAll(selector);
    const sources = source.querySelectorAll(selector);
    if (targets.length !== sources.length) return false;
    targets.forEach((node, index) => fn(node, sources[index]));
    return true;
  };

  let ok = true;
  ATTRIBUTE_SELECTORS.forEach((selector) => {
    ok = zip(selector, (t, s) => sugarCartSyncAttributes(t, s)) && ok;
  });
  CONTENT_SELECTORS.forEach((selector) => {
    ok = zip(selector, (t, s) => {
      sugarCartSyncAttributes(t, s);
      if (t.innerHTML !== s.innerHTML) t.innerHTML = s.innerHTML;
    }) && ok;
  });

  if (!ok) target.replaceWith(source.cloneNode(true));
}

class SugarCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.activeElement = null;
    this.testMode = this.dataset.testMode === 'true';
    this.sectionId = this.dataset.sectionId || 'sugar-cart-drawer';
    this.container = null;
    this.panel = null;
    this.cartUpdateRequestId = 0;
    this.refreshRequestId = 0;
    this.refreshTimer = null;
    this.refreshPromise = null;
    this.refreshWantsOpen = false;
    this.expectedCartSignature = null;
    this.openFinalizeTimeout = null;
    this.handleOpenTransitionEnd = null;

    // Bind methods
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onContainerClick = this.onContainerClick.bind(this);
  }

  connectedCallback() {
    if (window.__swOk === false) { this.removeAttribute('open'); this.style.display = 'none'; return; }
    this.refreshElementRefs();

    // Set up event listeners
    // Note: keydown listener is added/removed in open/close methods to avoid listening when closed
    // Use container click with event delegation for overlay clicks (more reliable than direct overlay listener)
    if (this.container) {
      this.container.removeEventListener('click', this.onContainerClick);
      this.container.addEventListener('click', this.onContainerClick);
    }

    // Set up cart icon click handler
    this.setHeaderCartIconAccessibility();

    // Subscribe to cart updates. Sources that render the drawer themselves
    // (renderContents / refresh) are skipped so one change paints once.
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const source = event?.source;
      if (
        source === 'sugar-cart-items' ||
        source === 'product-form' ||
        source === 'sticky-product' ||
        source === 'sticky-navigation' ||
        source === 'offers' ||
        source === 'product-upsells'
      ) return;
      this.onCartUpdate(event);
    });

    // Initialize any enhancements inside the drawer (slider/countdown, etc.)
    initSugarCartDrawerEnhancements(this);

    this.setupSheetSwipe();

    // Test mode - keep drawer open
    if (this.testMode && Shopify.designMode) {
      setTimeout(() => this.open(), 100);
    }
  }

  disconnectedCallback() {
    this.cartUpdateRequestId += 1;
    this.refreshRequestId += 1;

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }

    if (this.container) {
      this.container.removeEventListener('click', this.onContainerClick);
    }

    if (this.openFinalizeTimeout) {
      clearTimeout(this.openFinalizeTimeout);
      this.openFinalizeTimeout = null;
    }

    if (this.handleOpenTransitionEnd) {
      this.removeEventListener('transitionend', this.handleOpenTransitionEnd);
      this.handleOpenTransitionEnd = null;
    }

    // Clean up document-level listener if drawer was open when disconnected
    document.removeEventListener('keydown', this.onKeyUp);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('.header__icon--cart');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  onKeyUp(event) {
    if (event.code === 'Escape' && this.classList.contains('active')) {
      event.preventDefault();
      this.close();
    }
  }

  onContainerClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Handle close button clicks via delegation so button listeners never go stale.
    if (target.closest('.sugar-cart-drawer__close')) {
      event.preventDefault();
      this.close();
      return;
    }

    // Close drawer when clicking outside the panel. A handler that ran earlier on the
    // same click may have re-rendered what was tapped (an upsell's Add label becomes
    // Added), so the target can already be detached here; the path the event travelled
    // is what says where the tap landed.
    const currentPanel = this.querySelector('.sugar-cart-drawer__panel');
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const insidePanel = !!currentPanel && (path.length ? path.includes(currentPanel) : currentPanel.contains(target));
    if (!insidePanel) {
      this.close();
    }
  }

  open(triggeredBy) {
    this.refreshElementRefs();
    this.loadDeferredMedia();

    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    // Prevent body scroll immediately even if the drawer is already open.
    document.body.classList.add('sugar-cart-drawer-open', 'overflow-hidden');

    // Keep open idempotent when multiple add-to-cart handlers call it.
    if (this.classList.contains('active')) {
      refreshSugarCartAnnouncements(this);
      return;
    }

    // Add document-level keyboard listener for Escape key
    document.removeEventListener('keydown', this.onKeyUp);
    document.addEventListener('keydown', this.onKeyUp);

    if (this.openFinalizeTimeout) {
      clearTimeout(this.openFinalizeTimeout);
      this.openFinalizeTimeout = null;
    }

    if (this.handleOpenTransitionEnd) {
      this.removeEventListener('transitionend', this.handleOpenTransitionEnd);
      this.handleOpenTransitionEnd = null;
    }

    const finalizeOpen = () => {
      if (!this.classList.contains('active')) return;

      // Ensure sliders/layout measure correctly once the drawer is visible
      refreshSugarCartAnnouncements(this);

      // A marquee in the body or footer sized itself against a panel that was
      // parked off-canvas. Mount it once, scoped to this drawer so the rest of
      // the page's strips keep running; a cart update never re-renders these.
      if (!this.marqueesMounted && window.SugarMarquee && typeof window.SugarMarquee.refresh === 'function') {
        this.marqueesMounted = true;
        window.SugarMarquee.refresh(this);
      }

      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.sugar-cart-drawer__empty')
        : this.querySelector('#SugarCartDrawer');

      const focusElement = this.querySelector('.sugar-cart-drawer__panel') ||
                          this.querySelector('.sugar-cart-drawer__close');

      if (typeof trapFocus === 'function' && containerToTrapFocusOn) {
        trapFocus(containerToTrapFocusOn, focusElement);
      }
    };

    const currentPanel = this.panel || this.querySelector('.sugar-cart-drawer__panel');
    if (currentPanel) {
      this.handleOpenTransitionEnd = (transitionEvent) => {
        if (transitionEvent.target !== currentPanel) return;

        finalizeOpen();

        if (this.openFinalizeTimeout) {
          clearTimeout(this.openFinalizeTimeout);
          this.openFinalizeTimeout = null;
        }

        if (this.handleOpenTransitionEnd) {
          this.removeEventListener('transitionend', this.handleOpenTransitionEnd);
          this.handleOpenTransitionEnd = null;
        }
      };

      this.addEventListener('transitionend', this.handleOpenTransitionEnd);
    }

    // Fallback when transitionend is skipped due rapid DOM/state changes.
    this.openFinalizeTimeout = setTimeout(() => {
      finalizeOpen();
      this.openFinalizeTimeout = null;

      if (this.handleOpenTransitionEnd) {
        this.removeEventListener('transitionend', this.handleOpenTransitionEnd);
        this.handleOpenTransitionEnd = null;
      }
    }, 380);

    // Trigger animation
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        this.classList.add('animate', 'active');
        // Initialize slider early while panel slides in (element is visible and measurable now)
        requestAnimationFrame(() => refreshSugarCartAnnouncements(this));
      });
    } else {
      setTimeout(() => {
        this.classList.add('animate', 'active');
        setTimeout(() => refreshSugarCartAnnouncements(this));
      });
    }
  }

  close() {
    if (this.openFinalizeTimeout) {
      clearTimeout(this.openFinalizeTimeout);
      this.openFinalizeTimeout = null;
    }

    if (this.handleOpenTransitionEnd) {
      this.removeEventListener('transitionend', this.handleOpenTransitionEnd);
      this.handleOpenTransitionEnd = null;
    }

    this.classList.remove('active');

    // Remove document-level keyboard listener
    document.removeEventListener('keydown', this.onKeyUp);

    // Restore focus
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    // Restore body scroll
    document.body.classList.remove('sugar-cart-drawer-open', 'overflow-hidden');
  }

  /* A bottom sheet (Mobile style) closes with a swipe down from its handle or header. The
     panel follows the finger through --scd-drag; a drag past a third of the panel, or a
     quick flick, closes it, anything less springs back. Listeners sit on the drawer
     itself, so re-rendered contents need nothing re-attached. */
  setupSheetSwipe() {
    if (this.sheetSwipeBound || !this.classList.contains('sugar-cart-drawer--sheet-m')) return;
    this.sheetSwipeBound = true;
    const phone = window.matchMedia('(max-width: 749px)');
    let startY = 0;
    let lastY = 0;
    let lastT = 0;
    let velocity = 0;
    let dragging = false;
    let pointerId = null;

    const end = () => {
      if (!dragging) return;
      dragging = false;
      const dy = Math.max(0, lastY - startY);
      const panel = this.querySelector('.sugar-cart-drawer__panel');
      const height = panel ? panel.getBoundingClientRect().height : 0;
      this.classList.remove('is-dragging');
      this.style.removeProperty('--scd-drag');
      if (dy > height / 3 || velocity > 0.6) this.close();
    };

    this.addEventListener('pointerdown', (event) => {
      if (!phone.matches || !this.classList.contains('active') || event.button > 0) return;
      const target = event.target instanceof Element ? event.target : null;
      if (!target || !target.closest('.sugar-cart-drawer__handle, .sugar-cart-header-block')) return;
      if (target.closest('button, a, input, select, textarea')) return;
      dragging = true;
      pointerId = event.pointerId;
      startY = lastY = event.clientY;
      lastT = event.timeStamp;
      velocity = 0;
    });

    this.addEventListener('pointermove', (event) => {
      if (!dragging || event.pointerId !== pointerId) return;
      const dy = Math.max(0, event.clientY - startY);
      if (dy > 0 && !this.classList.contains('is-dragging')) this.classList.add('is-dragging');
      const dt = event.timeStamp - lastT;
      if (dt > 0) velocity = (event.clientY - lastY) / dt;
      lastY = event.clientY;
      lastT = event.timeStamp;
      this.style.setProperty('--scd-drag', dy + 'px');
    });

    this.addEventListener('pointerup', end);
    this.addEventListener('pointercancel', end);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  refreshElementRefs() {
    this.container = this.querySelector('#SugarCartDrawer');
    this.panel = this.querySelector('.sugar-cart-drawer__panel');
    // A re-render while open (last item removed) brings back the empty state.
    if (this.classList.contains('active')) this.loadDeferredMedia();
  }

  // The empty-state picture is held in data-src while the drawer is closed.
  loadDeferredMedia() {
    this.querySelectorAll('.sugar-cart-drawer__empty-image[data-src]').forEach((img) => {
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
        img.removeAttribute('data-srcset');
      }
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    });
  }

  /**
   * Render updated cart contents from an add-to-cart / cart API response.
   * @param {Object} parsedState - Response containing `sections` (drawer + bubble HTML)
   */
  renderContents(parsedState) {
    if (!parsedState) {
      this.open();
      return;
    }

    this.productId = parsedState.id;

    this.refresh({
      sections: parsedState.sections,
      cartData: window.SugarCart.isCart(parsedState) ? parsedState : null,
      open: true,
    });
  }

  /**
   * The one way the drawer repaints. Uses the section HTML handed over when a
   * mutation already rendered it; otherwise fetches drawer + bubble in a single
   * request. Calls made in the same burst share one fetch, and a response that
   * lands after a newer one has been requested is dropped.
   * @param {Object} options - { sections, cartData, open }
   * @returns {Promise}
   */
  refresh(options = {}) {
    if (options.cartData) {
      window.SugarCart.set(options.cartData);
      this.expectedCartSignature = `${options.cartData.item_count}:${options.cartData.total_price}`;
    } else {
      window.SugarCart.invalidate();
      this.expectedCartSignature = null;
    }

    const sections = options.sections;
    if (sections && typeof sections[this.sectionId] === 'string') {
      this.refreshRequestId += 1;
      this.applySections(sections);
      if (options.open) this.open();
      return Promise.resolve(true);
    }

    return this.scheduleRefresh(!!options.open);
  }

  scheduleRefresh(open) {
    if (open) this.refreshWantsOpen = true;

    if (!this.refreshTimer) {
      this.refreshPromise = new Promise((resolve) => {
        this.refreshTimer = setTimeout(() => {
          this.refreshTimer = null;
          this.fetchAndApply().then(resolve, resolve);
        }, 30);
      });
    }

    return this.refreshPromise;
  }

  fetchAndApply(attempt = 0) {
    const requestId = ++this.refreshRequestId;
    const url = `${routes.cart_url}?sections=${sugarCartSectionIds().join(',')}`;

    return fetch(url)
      .then((response) => response.json())
      .then((sections) => {
        if (requestId !== this.refreshRequestId) return false;
        // A render that predates the cart we already know about is stale:
        // ask again before painting it.
        if (this.expectedCartSignature && attempt < 2) {
          const html = sections && sections[this.sectionId];
          const match = typeof html === 'string' && html.match(/data-cart-sig="([^"]*)"/);
          if (match && match[1] !== this.expectedCartSignature) {
            return new Promise((resolve) => setTimeout(resolve, 250 + attempt * 350))
              .then(() => (requestId === this.refreshRequestId ? this.fetchAndApply(attempt + 1) : false));
          }
        }
        this.applySections(sections);
        if (this.refreshWantsOpen) {
          this.refreshWantsOpen = false;
          this.open();
        }
        return true;
      })
      .catch((error) => {
        if (requestId !== this.refreshRequestId) return false;
        console.error('Error updating cart drawer:', error);
        return false;
      });
  }

  applySections(sections) {
    const html = sections && sections[this.sectionId];
    if (typeof html === 'string') {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const source = doc.querySelector('sugar-cart-drawer');
      if (source) this.patchFrom(source);
    }

    if (sections && Object.prototype.hasOwnProperty.call(sections, 'cart-icon-bubble')) {
      this.refreshCartBubble(sections);
    }
  }

  /**
   * Patch the live drawer from a freshly rendered copy. Only the parts whose
   * Liquid depends on the cart are rewritten (header count, line items,
   * subtotals, checkout, installments, custom liquid, empty state); the
   * progress bar is morphed so it animates; everything with client state —
   * toggles, upsells, media, announcements, discount input — stays mounted.
   */
  patchFrom(source) {
    const targetPanel = this.querySelector('.sugar-cart-drawer__panel');
    const sourcePanel = source.querySelector('.sugar-cart-drawer__panel');
    if (!targetPanel || !sourcePanel) {
      this.replaceAllFrom(source);
      return;
    }

    // Empty state
    const sourceEmpty = sourcePanel.querySelector(':scope > .sugar-cart-drawer__empty');
    const targetEmpty = targetPanel.querySelector(':scope > .sugar-cart-drawer__empty');
    if (sourceEmpty && !targetEmpty) {
      targetPanel.insertBefore(sourceEmpty.cloneNode(true), targetPanel.firstChild);
    } else if (!sourceEmpty && targetEmpty) {
      targetEmpty.remove();
    }
    this.classList.toggle('is-empty', source.classList.contains('is-empty'));

    // Header (the count lives in the title)
    this.syncInner('.sugar-cart-header', source);

    // Progress bars
    this.syncProgress(source);

    // Line items
    const sourceItemsWrapper = source.querySelector('sugar-cart-items');
    const targetItemsWrapper = this.querySelector('sugar-cart-items');
    if (sourceItemsWrapper && targetItemsWrapper) {
      targetItemsWrapper.classList.toggle('is-empty', sourceItemsWrapper.classList.contains('is-empty'));
      this.syncInner('.sugar-cart-drawer__items', source);
    } else if (sourceItemsWrapper && !targetItemsWrapper) {
      const body = this.querySelector('.sugar-cart-drawer__body') || targetPanel;
      body.appendChild(sourceItemsWrapper.cloneNode(true));
    }

    // Merchant Liquid may read the cart
    this.syncInner('.custom-liquid-block', source);

    // Footer
    this.syncFooter(source);

    this.refreshElementRefs();
  }

  syncInner(selector, source) {
    const targets = this.querySelectorAll(selector);
    const sources = source.querySelectorAll(selector);
    if (targets.length !== sources.length) return false;
    targets.forEach((target, index) => {
      const next = sources[index].innerHTML;
      if (target.innerHTML !== next) target.innerHTML = next;
    });
    return true;
  }

  syncProgress(source) {
    const targets = this.querySelectorAll('sugar-cart-progress');
    const sources = source.querySelectorAll('sugar-cart-progress');
    if (targets.length !== sources.length) {
      this.syncInner('.sugar-cart-progress-container', source);
      return;
    }
    targets.forEach((target, index) => sugarCartMorphProgress(target, sources[index]));
  }

  syncFooter(source) {
    const targetFooter = this.querySelector('.sugar-cart-drawer__footer');
    const sourceFooter = source.querySelector('.sugar-cart-drawer__footer');

    if (!targetFooter) {
      if (sourceFooter) {
        const panel = this.querySelector('.sugar-cart-drawer__panel');
        if (panel) panel.appendChild(sourceFooter.cloneNode(true));
      }
      return;
    }
    if (!sourceFooter) return;

    // Toggles and the discount input keep their own state and stay mounted;
    // payment badges and icon lists never change with the cart.
    ['.sugar-cart-subtotals', '.sugar-cart-checkout-button', '[data-sugar-installments]'].forEach((selector) => {
      const sourceBlock = sourceFooter.querySelector(selector);
      const targetBlock = targetFooter.querySelector(selector);

      if (sourceBlock && targetBlock) {
        if (targetBlock.innerHTML !== sourceBlock.innerHTML) targetBlock.innerHTML = sourceBlock.innerHTML;
        return;
      }
      if (sourceBlock && !targetBlock) {
        targetFooter.appendChild(sourceBlock.cloneNode(true));
        return;
      }
      if (!sourceBlock && targetBlock) {
        targetBlock.remove();
      }
    });
  }

  // Last resort when the live markup has no panel to patch (legacy shells).
  replaceAllFrom(source) {
    const target = this.querySelector('#SugarCartDrawer');
    const next = source.querySelector('#SugarCartDrawer');
    if (!target || !next) return;
    target.innerHTML = next.innerHTML;
    this.classList.toggle('is-empty', source.classList.contains('is-empty'));
    this.refreshElementRefs();
    initSugarCartDrawerEnhancements(this);
    if (this.classList.contains('active')) initSugarCartAnnouncements(this);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const element = doc.querySelector(selector);
    return element ? element.innerHTML : '';
  }

  refreshCartBubble(sections) {
    var target = document.getElementById('cart-icon-bubble');
    if (!target) return;

    var html = sections && sections['cart-icon-bubble'];
    if (html !== undefined) {
      var doc = new DOMParser().parseFromString(html || '<div></div>', 'text/html');
      var bubble = doc.querySelector('.cart-count-bubble');
      this.applyCartBubble(target, bubble);
      return;
    }

    var requestId = ++this.cartUpdateRequestId;
    var self = this;
    fetch(routes.cart_url + '?section_id=cart-icon-bubble')
      .then(function (r) { return r.text(); })
      .then(function (responseHtml) {
        if (requestId !== self.cartUpdateRequestId) return;
        var doc = new DOMParser().parseFromString(responseHtml, 'text/html');
        var bubble = doc.querySelector('.cart-count-bubble');
        self.applyCartBubble(target, bubble);
      })
      .catch(function () {});
  }

  applyCartBubble(target, bubble) {
    if (!bubble && target.hasAttribute('data-show-empty-cart-count')) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      const count = document.createElement('span');
      count.setAttribute('aria-hidden', 'true');
      count.textContent = '0';
      const label = document.createElement('span');
      label.className = 'visually-hidden';
      label.textContent = target.dataset.emptyCartLabel || '';
      bubble.append(count, label);
    }
    target.innerHTML = bubble ? bubble.outerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: this.sectionId,
        selector: '#SugarCartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  /**
   * Handle cart update events from PubSub
   */
  onCartUpdate(event) {
    this.refresh({
      sections: event && event.sections,
      cartData: event && window.SugarCart.isCart(event.cartData) ? event.cartData : null,
    });
  }
}

customElements.define('sugar-cart-drawer', SugarCartDrawer);

function initSugarCartDrawerEnhancements(rootEl) {
  if (!rootEl || !(rootEl instanceof HTMLElement)) return;
  // Don't initialize announcements slider here - wait until drawer is visible
  initSugarCartDrawerCountdownTimers(rootEl);
  initSugarCartSophisticatedCountdowns(rootEl);
  // Re-rendered markup arrives without its interactive engines attached —
  // rotators ([data-rotate]) only boot on DOMContentLoaded otherwise.
  if (window.SugarInteractive && typeof window.SugarInteractive.init === 'function') {
    window.SugarInteractive.init(rootEl);
  }
}

function refreshSugarCartAnnouncements(rootEl) {
  if (!rootEl || !(rootEl instanceof HTMLElement)) return;

  const containers = rootEl.querySelectorAll('[data-sugar-cart-announcements]');
  if (!containers.length) return;

  containers.forEach((container) => {
    const splideEl = container.querySelector('[data-sugar-cart-announcements-splide]');
    if (!splideEl) return;

    // If already initialized, do nothing (don't refresh - it causes jitter)
    if (splideEl._splideInstance || splideEl._splideInitializing) {
      return;
    }

    // Mark as initializing to prevent duplicate attempts
    splideEl._splideInitializing = true;

    // Initialize for the first time (drawer is now visible)
    initSugarCartAnnouncements(rootEl);
  });
}

function initSugarCartAnnouncements(rootEl, attempt = 0) {
  const containers = rootEl.querySelectorAll('[data-sugar-cart-announcements]');
  if (!containers.length) return;

  containers.forEach((container) => {
    const splideEl = container.querySelector('[data-sugar-cart-announcements-splide]');
    if (container.dataset.enableSlider === 'false' || !splideEl) return;

    // Skip if already initialized
    if (splideEl._splideInstance) return;

    // Wait for Splide (loaded on demand by vendor-runtime-loader.js)
    if (typeof Splide === 'undefined') {
      if (attempt < 200) {
        setTimeout(() => initSugarCartAnnouncements(rootEl, attempt + 1), 50);
      }
      return;
    }

    // Ensure required classes exist for Splide.
    // Only tag <li> children as slides — integrity guards inject sibling
    // <style>/<script> nodes which Splide would otherwise render as visible
    // "slides", surfacing their source text inside the cart drawer.
    const listEl = splideEl.querySelector('.splide__list');
    if (listEl) {
      Array.from(listEl.children).forEach((child) => {
        if (child && child.tagName === 'LI' && child.classList) {
          child.classList.add('splide__slide');
        }
      });
    }

    const slideCount = parseInt(container.dataset.slideCount || '0', 10) || 0;
    const autoplay = container.dataset.autoplay === 'true';
    const autoplaySpeed = (parseInt(container.dataset.autoplaySpeed || '5', 10) || 5) * 1000;
    const pauseOnHover = container.dataset.pauseOnHover === 'true';
    const showArrows = container.dataset.showArrows === 'true';
    const arrowColorMode = container.dataset.arrowColor || 'auto';
    const fitSlideHeight = container.dataset.slideHeight === 'fit';

    const options = {
      type: 'loop',
      perPage: 1,
      perMove: 1,
      pagination: false,
      arrows: showArrows && slideCount > 1,
      autoplay: autoplay && slideCount > 1,
      interval: autoplaySpeed,
      pauseOnHover: pauseOnHover,
      pauseOnFocus: true,
      drag: slideCount > 1,
      speed: 400,
      easing: 'ease',
      gap: 0,
      width: '100%',
      fixedWidth: false,
      autoWidth: false
    };

    const splide = new Splide(splideEl, options);

    function updateArrowColor() {
      if (arrowColorMode !== 'auto') return;

      const slideObj = splide.Components?.Slides?.getAt(splide.index);
      const activeSlide = slideObj?.slide || container.querySelector('.splide__slide.is-active');
      const textColor = activeSlide?.dataset?.textColor;

      if (textColor) {
        container.style.setProperty('--sugar-cart-announcements-arrow-color', textColor);
      }
    }

    const trackEl = splideEl.querySelector('.splide__track');

    function updateTrackBackground(nextIndex) {
      if (!trackEl) return;

      const index = typeof nextIndex === 'number' ? nextIndex : splide.index;
      const slideObj = splide.Components?.Slides?.getAt(index);
      const slide = slideObj?.slide;
      // Support both announcement items and countdown slides
      const inner = slide?.querySelector('.sugar-cart-announcement-item__inner') ||
                    slide?.querySelector('.sugar-cart-announcement-countdown__inner') ||
                    slide?.querySelector('.sugar-cart-announcement-rotating-text__inner');

      if (inner) {
        const bg = getComputedStyle(inner).backgroundColor;
        if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
          trackEl.style.backgroundColor = bg;
        }
      }
    }

    splide.on('mounted', () => updateTrackBackground());
    splide.on('move', (newIndex) => updateTrackBackground(newIndex));

    // "Fit each slide": the track follows the active slide's own height (the list keeps every
    // slide at its natural height, the track clips and animates between them)
    function fitTrackHeight(nextIndex) {
      if (!trackEl) return;
      const index = typeof nextIndex === 'number' ? nextIndex : splide.index;
      const slide = splide.Components?.Slides?.getAt(index)?.slide;
      if (slide) trackEl.style.height = `${slide.offsetHeight}px`;
    }

    if (fitSlideHeight) {
      splide.on('mounted refresh resized', () => fitTrackHeight());
      splide.on('move', (newIndex) => fitTrackHeight(newIndex));
      splideEl.querySelectorAll('img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', () => fitTrackHeight(), { once: true });
      });
    }

    if (arrowColorMode === 'auto') {
      splide.on('mounted', updateArrowColor);
      splide.on('move', updateArrowColor);
      splide.on('updated', updateArrowColor);
    }

    splide.mount();
    splideEl._splideInstance = splide;

    // Refresh after a short delay to ensure proper loop initialization
    requestAnimationFrame(() => {
      if (splideEl._splideInstance) {
        splideEl._splideInstance.refresh();
      }
    });
  });
}

function initSugarCartDrawerCountdownTimers(rootEl) {
  // One interval for the entire cart drawer to avoid leaks on re-render.
  if (window.__sugarCartDrawerCountdownIntervalId) {
    clearInterval(window.__sugarCartDrawerCountdownIntervalId);
    window.__sugarCartDrawerCountdownIntervalId = null;
  }

  const timers = Array.from(rootEl.querySelectorAll('[data-countdown-timer]'));
  if (!timers.length) return;

  const timerStates = timers.map((timer) => {
    const hoursEl = timer.querySelector('[data-countdown-hours]');
    const minutesEl = timer.querySelector('[data-countdown-minutes]');
    const secondsEl = timer.querySelector('[data-countdown-seconds]');

    const initialHours = parseInt(timer.dataset.initialHours, 10) || 0;
    const initialMinutes = parseInt(timer.dataset.initialMinutes, 10) || 0;
    const initialSeconds = parseInt(timer.dataset.initialSeconds, 10) || 0;
    const shouldRestart = timer.dataset.restart === 'restart';

    let totalSeconds = (initialHours * 3600) + (initialMinutes * 60) + initialSeconds;
    const originalTotal = totalSeconds;

    function updateDisplay() {
      const hrs = Math.floor(totalSeconds / 3600);
      // With no hours element, fold the hours into the minutes so the
      // displayed time stays correct (1h30m reads 90:00, not 30:00).
      const mins = hoursEl
        ? Math.floor((totalSeconds % 3600) / 60)
        : Math.floor(totalSeconds / 60);
      const secs = totalSeconds % 60;

      if (hoursEl) hoursEl.textContent = String(hrs).padStart(2, '0');
      if (minutesEl) minutesEl.textContent = String(mins).padStart(2, '0');
      if (secondsEl) secondsEl.textContent = String(secs).padStart(2, '0');
    }

    updateDisplay();

    return {
      tick: () => {
        if (totalSeconds > 0) {
          totalSeconds -= 1;
          updateDisplay();
          return;
        }
        if (shouldRestart) {
          totalSeconds = originalTotal;
          updateDisplay();
        }
      }
    };
  });

  window.__sugarCartDrawerCountdownIntervalId = setInterval(() => {
    timerStates.forEach((state) => state.tick());
  }, 1000);
}

function initSugarCartSophisticatedCountdowns(rootEl) {
  // Clear any existing interval for sophisticated countdowns
  if (window.__sugarCartSophisticatedCountdownIntervalId) {
    clearInterval(window.__sugarCartSophisticatedCountdownIntervalId);
    window.__sugarCartSophisticatedCountdownIntervalId = null;
  }

  // Pin the host element so each interval tick re-queries from the current cart drawer DOM.
  // Re-querying on every tick is cheap and keeps the timer in sync regardless of how many
  // times the drawer's contents are replaced.
  const host = rootEl || document.querySelector('sugar-cart-drawer') || document;

  function pad(num) {
    return String(num).padStart(2, '0');
  }

  function parseDateParts(str) {
    if (!str) return null;
    const m = str.match(/^(\d{4})-(\d{2})-(\d{2})[\sT](\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (!m) return null;
    return { y: +m[1], mo: +m[2] - 1, d: +m[3], h: +m[4], mi: +m[5], s: +(m[6] || 0) };
  }

  // Per-timer state keyed by the element itself so auto_renew end time persists
  // across ticks even though we don't keep the timer node in a closure.
  const stateByTimer = new WeakMap();

  function getState(timer) {
    let state = stateByTimer.get(timer);
    if (!state) {
      state = { autoRenewEndTime: null };
      stateByTimer.set(timer, state);
    }
    return state;
  }

  function getTargetDate(timer) {
    const state = getState(timer);
    const type = timer.dataset.countdownType;
    const timezoneMode = timer.dataset.timezoneMode;
    const utcOffset = parseInt(timer.dataset.utcOffset, 10) || 0;

    if (type === 'specific_date') {
      const p = parseDateParts(timer.dataset.endDate);
      if (!p) return null;

      let targetDate;
      if (timezoneMode === 'fixed') {
        targetDate = new Date(Date.UTC(p.y, p.mo, p.d, p.h, p.mi, p.s) - utcOffset * 3600000);
      } else {
        targetDate = new Date(p.y, p.mo, p.d, p.h, p.mi, p.s);
      }
      if (isNaN(targetDate.getTime())) return null;
      return targetDate;
    }

    if (type === 'every_24_hours') {
      const now = new Date();
      let midnight;

      if (timezoneMode === 'fixed') {
        const utcMidnight = new Date(
          Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0, 0)
        );
        midnight = new Date(utcMidnight.getTime() - utcOffset * 60 * 60 * 1000);
      } else {
        midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
      }
      return midnight;
    }

    if (type === 'auto_renew') {
      if (!state.autoRenewEndTime) {
        const autoDays = parseInt(timer.dataset.autoDays, 10) || 0;
        const autoHours = parseInt(timer.dataset.autoHours, 10) || 0;
        const autoMinutes = parseInt(timer.dataset.autoMinutes, 10) || 0;
        const autoSeconds = parseInt(timer.dataset.autoSeconds, 10) || 0;
        const totalMs =
          autoDays * 24 * 60 * 60 * 1000 +
          autoHours * 60 * 60 * 1000 +
          autoMinutes * 60 * 1000 +
          autoSeconds * 1000;
        state.autoRenewEndTime = new Date(Date.now() + totalMs);
      }
      return state.autoRenewEndTime;
    }

    return null;
  }

  function updateDisplay(timer, days, hours, minutes, seconds) {
    const daysEl = timer.querySelector('[data-days]');
    const hoursEl = timer.querySelector('[data-hours]');
    const minutesEl = timer.querySelector('[data-minutes]');
    const secondsEl = timer.querySelector('[data-seconds]');
    if (daysEl) daysEl.textContent = pad(days);
    if (hoursEl) hoursEl.textContent = pad(hours);
    if (minutesEl) minutesEl.textContent = pad(minutes);
    if (secondsEl) secondsEl.textContent = pad(seconds);
  }

  function tickOne(timer) {
    // Skip detached timers — happens briefly while markup is being swapped.
    if (!timer.isConnected) return;

    const type = timer.dataset.countdownType;
    const target = getTargetDate(timer);
    if (!target) {
      updateDisplay(timer, 0, 0, 0, 0);
      return;
    }

    const now = new Date();
    let diff = target - now;

    if (diff <= 0) {
      if (type === 'specific_date') {
        timer.classList.add('countdown-timer--ended');
        return;
      } else if (type === 'every_24_hours' || type === 'auto_renew') {
        getState(timer).autoRenewEndTime = null;
        tickOne(timer);
        return;
      }
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    diff -= days * 1000 * 60 * 60 * 24;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    diff -= hours * 1000 * 60 * 60;
    const minutes = Math.floor(diff / (1000 * 60));
    diff -= minutes * 1000 * 60;
    const seconds = Math.floor(diff / 1000);

    updateDisplay(timer, days, hours, minutes, seconds);
  }

  function tickAll() {
    const timers = host.querySelectorAll
      ? host.querySelectorAll('[data-sophisticated-countdown]')
      : [];
    if (!timers.length) return;
    timers.forEach(tickOne);
  }

  // Run immediately so the visible spans flip from "00" to their real values within the
  // same frame the drawer slides in (not on the next 1-second tick).
  tickAll();

  window.__sugarCartSophisticatedCountdownIntervalId = setInterval(tickAll, 1000);
}


/**
 * Sugar Cart Items
 * Handles cart item quantity updates and removals
 */
class SugarCartItems extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.updateRequestId = 0;
    // Each line debounces on its own and never has more than one request on
    // the wire: a change made while its request is in flight is sent after.
    this.pendingTimers = new Map();
    this.inflight = new Map();
    this.queued = new Map();

    this.onChange = this.onChange.bind(this);
    this.addEventListener('change', this.onChange);

    // Quantity/remove clicks are delegated at WINDOW capture, not bound per
    // button and not delegated from this element: cart apps clone/replace the
    // item buttons (stripping per-button listeners) AND some hijack clicks
    // with document-capture handlers of their own (turning a quantity click
    // into an add-to-cart + /cart navigation). Window capture fires before
    // document capture, so we see our buttons' clicks first, handle them, and
    // stop the event from reaching anything else.
    this.windowClickHandler = (event) => this.onDelegatedClick(event);
    window.addEventListener('click', this.windowClickHandler, true);
  }

  disconnectedCallback() {
    if (this.windowClickHandler) {
      window.removeEventListener('click', this.windowClickHandler, true);
    }
    this.pendingTimers.forEach((timer) => clearTimeout(timer));
    this.pendingTimers.clear();
  }

  get lineItemStatusElement() {
    return this.querySelector('[data-cart-line-status]') || document.getElementById('SugarCartDrawer-LineItemStatus');
  }

  onDelegatedClick(event) {
    const target = event.target;
    if (!target || !target.closest) return;

    const removeButton = target.closest('.sugar-cart-item__remove');
    if (removeButton && this.contains(removeButton)) {
      if (removeButton.disabled) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.submitQuantity({
        line: removeButton.dataset.index,
        key: removeButton.dataset.key,
        quantity: 0,
        variantId: removeButton.dataset.variantId,
      });
      return;
    }

    const upgradeButton = target.closest('[data-cart-upgrade]');
    if (upgradeButton && this.contains(upgradeButton)) {
      if (upgradeButton.disabled) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.submitPlanUpgrade(upgradeButton);
      return;
    }

    const quantityButton = target.closest('.sugar-cart-item__quantity-btn');
    if (!quantityButton || !this.contains(quantityButton) || quantityButton.disabled) return;
    event.preventDefault();
    event.stopImmediatePropagation();

    const quantityWrapper = quantityButton.closest('.sugar-cart-item__quantity');
    const input = quantityWrapper && quantityWrapper.querySelector('.sugar-cart-item__quantity-input');
    if (!input) return;

    const currentValue = parseInt(input.value, 10) || 0;
    const step = parseInt(input.step, 10) || 1;
    const min = parseInt(input.min, 10) || 0;
    const max = input.max ? parseInt(input.max, 10) : Infinity;

    let newValue;
    if (quantityButton.name === 'plus') {
      newValue = Math.min(currentValue + step, max);
    } else {
      newValue = Math.max(currentValue - step, min);
    }

    if (newValue !== currentValue) {
      input.value = newValue;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  onChange(event) {
    const input = event.target;
    if (!input || !input.classList || !input.classList.contains('sugar-cart-item__quantity-input')) return;

    const quantity = parseInt(input.value, 10);
    if (!Number.isFinite(quantity) || quantity < 0) {
      input.value = input.getAttribute('value');
      return;
    }

    this.scheduleQuantity({
      line: input.dataset.index,
      key: input.dataset.key,
      quantity,
      name: input.name,
      variantId: input.dataset.quantityVariantId,
    });
  }

  changeId(change) {
    return change.key || `line-${change.line}`;
  }

  scheduleQuantity(change) {
    const id = this.changeId(change);
    clearTimeout(this.pendingTimers.get(id));
    this.pendingTimers.set(id, setTimeout(() => {
      this.pendingTimers.delete(id);
      this.submitQuantity(change);
    }, ON_CHANGE_DEBOUNCE_TIMER));
  }

  submitQuantity(change) {
    const id = this.changeId(change);
    clearTimeout(this.pendingTimers.get(id));
    this.pendingTimers.delete(id);

    if (this.inflight.has(id)) {
      this.queued.set(id, change);
      return;
    }

    this.inflight.set(id, change);
    this.performQuantityUpdate(change).finally(() => {
      this.inflight.delete(id);
      const next = this.queued.get(id);
      if (next) {
        this.queued.delete(id);
        this.submitQuantity(next);
      }
    });
  }

  getSectionsToRender() {
    // The drawer, the header bubble and (when on screen) the cart page.
    return sugarCartSectionIds().map((section) => ({ id: section, section }));
  }

  isAutoAddedCartItem(item) {
    const properties = item?.properties || {};
    const hasOfferGiftFlag = Object.prototype.hasOwnProperty.call(properties, '_offer_gift');
    const hasProgressFlag = Object.prototype.hasOwnProperty.call(properties, '_sugar_progress_goal');

    return (
      properties._gift_list === 'true' ||
      properties._gift_unlock === 'true' ||
      hasOfferGiftFlag ||
      hasProgressFlag ||
      properties._cart_toggle === 'true' ||
      properties._cart_upsell === 'true'
    );
  }

  isProductBoundAutoGift(item) {
    const properties = item?.properties || {};
    return (
      properties._gift_list === 'true' ||
      properties._gift_unlock === 'true' ||
      Object.prototype.hasOwnProperty.call(properties, '_offer_gift')
    );
  }

  getOrphanedAutoGiftKeys(cartState) {
    if (!cartState || !Array.isArray(cartState.items)) return [];

    const qualifyingProductQuantities = new Map();
    let hasNonAutoItems = false;
    cartState.items.forEach((item) => {
      if (!this.isAutoAddedCartItem(item)) {
        hasNonAutoItems = true;
        const productId = String(item.product_id);
        const quantity = parseInt(item.quantity, 10) || 0;
        const existingQty = qualifyingProductQuantities.get(productId) || 0;
        qualifyingProductQuantities.set(productId, existingQty + quantity);
      }
    });

    const orphanedGiftKeys = [];
    cartState.items.forEach((item) => {
      if (!this.isProductBoundAutoGift(item)) return;

      const properties = item?.properties || {};
      const qualifyingProductId = String(properties._gift_for_product || '').trim();
      const qualifyingQty = qualifyingProductQuantities.get(qualifyingProductId) || 0;

      if (!qualifyingProductId || qualifyingQty <= 0) {
        if (item.key) orphanedGiftKeys.push(item.key);
        return;
      }

      // Gift Unlocks tiers can require a minimum qualifying quantity.
      if (properties._gift_unlock === 'true') {
        const requiredQty = parseInt(properties._gift_unlock_required_qty, 10);
        if (Number.isFinite(requiredQty) && requiredQty > 0 && qualifyingQty < requiredQty) {
          if (item.key) orphanedGiftKeys.push(item.key);
        }
      }
    });

    // If there are no regular cart items left, toggle-driven add-ons should be removed
    // so the drawer can transition to a true empty-cart state.
    if (!hasNonAutoItems) {
      cartState.items.forEach((item) => {
        const properties = item?.properties || {};
        if (
          item.key &&
          (properties._cart_toggle === 'true' || properties._cart_upsell === 'true')
        ) {
          orphanedGiftKeys.push(item.key);
        }
      });
    }

    // Progress-goal gifts whose goal is no longer met leave in the same
    // request instead of one round trip later.
    document.querySelectorAll('sugar-cart-progress').forEach((progress) => {
      if (typeof progress.getGiftKeysToRemove !== 'function') return;
      progress.getGiftKeysToRemove(cartState).forEach((key) => orphanedGiftKeys.push(key));
    });

    return [...new Set(orphanedGiftKeys)];
  }

  async removeOrphanedAutoGifts(cartState) {
    const orphanedGiftKeys = this.getOrphanedAutoGiftKeys(cartState);
    if (orphanedGiftKeys.length === 0) return null;

    const updates = {};
    orphanedGiftKeys.forEach((key) => {
      updates[key] = 0;
    });

    const body = JSON.stringify({
      updates,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    try {
      const result = await window.SugarCart.update(JSON.parse(body));
      return window.SugarCart.isCart(result) ? result : null;
    } catch (error) {
      console.error('Error removing orphaned auto gifts:', error);
      return null;
    }
  }

  sugarTrackQtyIncrease(item, delta) {
    const entity = item && item.product_id != null ? String(item.product_id) : '';
    if (!entity || !delta || delta < 1) return;
    const ctx = {
      path: window.location.pathname,
      product: item.handle || '',
      productTitle: item.product_title || item.title || '',
      section: 'cart_qty'
    };
    const args = ['atc', 'cart_qty', entity, '+' + delta, ctx];
    if (window.SugarAnalytics) window.SugarAnalytics.track.apply(null, args);
    else (window._sugarQ = window._sugarQ || []).push(args);
  }

  // Kept for callers that still use the positional signature.
  updateQuantity(line, quantity, event, name, variantId, lineItemKey) {
    return this.submitQuantity({ line, key: lineItemKey, quantity, name, variantId });
  }

  performQuantityUpdate(change) {
    const { line, key: lineItemKey, quantity, variantId } = change;
    const isRemoval = quantity === 0;
    const requestId = ++this.updateRequestId;

    // Capture the pre-change quantity so we can count a genuine increase.
    let sugarOldQty = 0;
    try {
      const qInput = this.querySelector(`[data-index="${line}"]`);
      if (qInput) sugarOldQty = parseInt(qInput.getAttribute('value'), 10) || 0;
    } catch (e) {}

    this.enableLoading(line);
    this.clearLiveRegions();

    const payload = {
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    };
    if (lineItemKey) {
      payload.id = lineItemKey;
    } else {
      payload.line = line;
    }
    const drawer = document.querySelector('sugar-cart-drawer');
    const repaintFromServer = () => {
      if (drawer && typeof drawer.refresh === 'function') drawer.refresh();
    };

    return window.SugarCart.change(payload)
      .then(async (result) => {
        let parsedState = result;

        if (!window.SugarCart.isCart(parsedState)) {
          const input = this.querySelector(`[data-index="${line}"]`);
          if (input) {
            input.value = input.getAttribute('value');
          }
          const message = parsedState.errors || parsedState.description || parsedState.message || window.cartStrings.error;
          this.updateLiveRegions(line, message);
          // Paint whatever the cart holds now, including an earlier change
          // that was waiting on this one.
          repaintFromServer();
          return;
        }

        const stateAfterGiftCleanup = await this.removeOrphanedAutoGifts(parsedState);
        if (stateAfterGiftCleanup) {
          parsedState = stateAfterGiftCleanup;
        }

        window.SugarCart.set(parsedState);

        // Only the newest change paints; an older response would show a cart
        // that has already moved on.
        const isLatest = requestId === this.updateRequestId;
        if (isLatest) {
          if (drawer && typeof drawer.refresh === 'function') {
            drawer.refresh({ sections: parsedState.sections, cartData: parsedState });
          } else {
            this.classList.toggle('is-empty', parsedState.item_count === 0);
          }
        }

        // Locate the changed line by its line-item KEY, never by positional index:
        // discounts and free-gift offers can split, merge or re-order lines.
        const changedItem = lineItemKey
          ? parsedState.items.find((cartItem) => cartItem.key === lineItemKey)
          : parsedState.items[line - 1];
        const deliveredQuantity = changedItem?.quantity;

        // Sugar Analytics: count a genuine quantity increase (Shopify actually
        // delivered more units than the line had before) as a cart-add action.
        if (
          !isRemoval &&
          changedItem &&
          typeof deliveredQuantity === 'number' &&
          deliveredQuantity > sugarOldQty
        ) {
          this.sugarTrackQtyIncrease(changedItem, deliveredQuantity - sugarOldQty);
        }

        // Surface the "you can only add N" notice only when Shopify delivered
        // FEWER units than requested (a real inventory / quantity-rule cap).
        let message = '';
        if (
          !isRemoval &&
          typeof deliveredQuantity === 'number' &&
          deliveredQuantity < quantity
        ) {
          message = window.cartStrings.quantityError.replace('[quantity]', deliveredQuantity);
        }

        this.updateLiveRegions(line, message);

        if (isLatest) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'sugar-cart-items',
            cartData: parsedState,
            sections: parsedState.sections,
            variantId: variantId
          });
        }
      })
      .catch((error) => {
        console.error('Error updating quantity:', error);
        this.querySelectorAll('.sugar-cart-item__loading-spinner').forEach((spinner) => {
          spinner.style.display = 'none';
        });
        const errors = this.querySelector('[data-cart-errors]') || document.getElementById('SugarCartDrawer-CartErrors');
        if (errors) {
          errors.textContent = window.cartStrings.error;
        }
        repaintFromServer();
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  // Moves one line onto a selling plan, keeping its quantity and properties.
  // Shopify re-keys a line when its plan changes, so nothing here holds on to the old
  // key past the request: the whole cart repaints from the sections it asks for.
  submitPlanUpgrade(button) {
    const line = button.dataset.index;
    const lineItemKey = button.dataset.key;
    const sellingPlanId = button.dataset.sellingPlanId;
    // The on-screen quantity, not the one baked in at render: a click landing before a
    // debounced quantity change has repainted would otherwise send the old number back.
    const row = button.closest('[data-line-index]');
    const quantityInput = row && row.querySelector('.sugar-cart-item__quantity-input');
    const quantity = parseInt((quantityInput && quantityInput.value) || button.dataset.quantity, 10) || 1;
    if (!lineItemKey || !sellingPlanId) return;

    const requestId = ++this.updateRequestId;
    const isSwitch = button.getAttribute('role') === 'switch';

    button.disabled = true;
    if (isSwitch) button.setAttribute('aria-checked', 'true');
    this.enableLoading(line);
    this.clearLiveRegions();

    const drawer = document.querySelector('sugar-cart-drawer');
    const repaintFromServer = () => {
      if (drawer && typeof drawer.refresh === 'function') drawer.refresh();
    };
    const restoreControl = () => {
      button.disabled = false;
      if (isSwitch) button.setAttribute('aria-checked', 'false');
    };

    return window.SugarCart.change({
      id: lineItemKey,
      quantity,
      selling_plan: sellingPlanId,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    })
      .then((result) => {
        if (!window.SugarCart.isCart(result)) {
          restoreControl();
          const message = result.errors || result.description || result.message || window.cartStrings.error;
          this.updateLiveRegions(line, message);
          repaintFromServer();
          return;
        }

        window.SugarCart.set(result);

        if (requestId !== this.updateRequestId) return;

        if (drawer && typeof drawer.refresh === 'function') {
          drawer.refresh({ sections: result.sections, cartData: result });
        }
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'sugar-cart-items',
          cartData: result,
          sections: result.sections,
          variantId: button.dataset.variantId,
        });
      })
      .catch((error) => {
        console.error('Error switching to a subscription:', error);
        restoreControl();
        const errors = this.querySelector('[data-cart-errors]') || document.getElementById('SugarCartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
        repaintFromServer();
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  clearLiveRegions() {
    this.querySelectorAll('.sugar-cart-item__error').forEach((lineItemError) => {
      const errorText = lineItemError.querySelector('.cart-item__error-text');
      if (errorText) {
        errorText.textContent = '';
      }
      lineItemError.classList.remove('is-active');
    });
  }

  updateLiveRegions(line, message) {
    const lineItemError = this.querySelector(`[data-line-index="${line}"] .sugar-cart-item__error`);
    if (lineItemError) {
      const errorText = lineItemError.querySelector('.cart-item__error-text');
      if (errorText) {
        errorText.textContent = message;
      }
      lineItemError.classList.toggle('is-active', Boolean(message));
    }

    const lineItemStatus = this.lineItemStatusElement;
    if (lineItemStatus) {
      lineItemStatus.setAttribute('aria-hidden', 'true');
    }

    const cartStatus = this.querySelector('[data-cart-live-region]') || document.getElementById('SugarCartDrawer-LiveRegionText');
    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', 'false');
      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', 'true');
      }, 1000);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML || '';
  }

  enableLoading(line) {
    const item = this.querySelector(`[data-line-index="${line}"]`);
    if (item) {
      item.classList.add('is-loading');
    }

    document.activeElement?.blur();

    const lineItemStatus = this.lineItemStatusElement;
    if (lineItemStatus) {
      lineItemStatus.setAttribute('aria-hidden', 'false');
    }
  }

  disableLoading(line) {
    const item = this.querySelector(`[data-line-index="${line}"]`);
    if (item) {
      item.classList.remove('is-loading');
    }
  }
}

customElements.define('sugar-cart-items', SugarCartItems);


/**
 * Sugar Cart Progress
 * Handles the progress bar with live updates
 */
class SugarCartProgress extends HTMLElement {
  constructor() {
    super();
    this.goals = [];
    this.parseGoals();
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      this.updateProgress(event.cartData, event.source);
    });

    // Sync initial bar widths from live cart data so mode toggles apply immediately.
    this.initialProgressSync();

    // Sugar Analytics: fire a "bar shown" impression once it's actually visible.
    this.setupSugarImpression();
  }

  async initialProgressSync() {
    try {
      const cartData = await this.fetchCart();
      this.updateProgress(cartData, 'initial-sync');
    } catch (e) {
      // Silent fail on initial sync
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this._sugarImpObserver) {
      this._sugarImpObserver.disconnect();
      this._sugarImpObserver = null;
    }
  }

  /* ── Sugar Analytics (cart_progress goal-reach) ────────────────────── */
  sugarTrack(type, detail, label) {
    const entity = this.getAttribute('data-sugar-entity');
    if (!entity) return;
    const ctx = { path: window.location.pathname, section: entity, label: label || '' };
    const args = [type, 'cart_progress', entity, detail || '', ctx];
    if (window.SugarAnalytics) window.SugarAnalytics.track.apply(null, args);
    else (window._sugarQ = window._sugarQ || []).push(args);
  }

  fireSugarImpression() {
    const entity = this.getAttribute('data-sugar-entity');
    if (!entity) return;
    let first = true;
    try {
      const key = '_sugar_imp_' + entity;
      if (sessionStorage.getItem(key)) first = false;
      else sessionStorage.setItem(key, '1');
    } catch (e) {}
    if (first) this.sugarTrack('impression', '', '');
  }

  setupSugarImpression() {
    const entity = this.getAttribute('data-sugar-entity');
    if (!entity) return;
    try {
      if (sessionStorage.getItem('_sugar_imp_' + entity)) return;
    } catch (e) {}
    if (typeof IntersectionObserver !== 'function') {
      this.fireSugarImpression();
      return;
    }
    this._sugarImpObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.fireSugarImpression();
          if (this._sugarImpObserver) {
            this._sugarImpObserver.disconnect();
            this._sugarImpObserver = null;
          }
          break;
        }
      }
    }, { threshold: 0.5 });
    this._sugarImpObserver.observe(this);
  }

  parseGoals() {
    // Parse goal data from data attributes
    // Note: data-goal-1-target becomes dataset['goal-1Target'] (hyphen before number stays)
    const goalCount = parseInt(this.dataset.goalCount, 10) || 1;
    this.fillGoalsIndependently = this.dataset.fillGoalsIndependently === 'true';
    this.autoAddProperty = '_sugar_progress_goal';
    this.processing = false;

    for (let i = 1; i <= goalCount; i++) {
      const type = this.dataset[`goal-${i}Type`];
      const variantId = this.dataset[`goal-${i}Variant`];
      const autocompleteSub = this.dataset[`goal-${i}AutocompleteSub`] === 'true';
      const target = parseInt(this.dataset[`goal-${i}Target`], 10) || 0;

      this.goals.push({
        type,
        threshold: target,
        target: target,
        reached: false,
        variantId: variantId ? parseInt(variantId, 10) : null,
        autocompleteSub,
        goalNumber: i,
        label: this.getAttribute('data-sugar-goal-' + i + '-label') || ''
      });
    }
  }

  updateProgress(cartData, source) {
    // Publishers such as the product form hand over the add response, not the
    // cart; compute from the real cart instead (one shared request).
    if (!window.SugarCart.isCart(cartData)) {
      if (this.pendingProgressSync) return;
      this.pendingProgressSync = window.SugarCart.get({ fresh: true })
        .then((cart) => { this.pendingProgressSync = null; if (window.SugarCart.isCart(cart)) this.updateProgress(cart, source); })
        .catch(() => { this.pendingProgressSync = null; });
      return;
    }

    // Off: the subtotal before order discounts, the amount Shopify checks an automatic
    // order discount's minimum against, so a matching goal stays reached once it applies.
    const afterDiscounts = this.getAttribute('data-after-discounts') !== 'false';
    const cartTotal = !afterDiscounts && cartData.items_subtotal_price != null
      ? cartData.items_subtotal_price
      : cartData.total_price;
    const cartItemCount = cartData.item_count;
    const hasSubscription = cartData.items.some((item) => item.selling_plan_allocation);
    const fillGoalsIndependently = (this.dataset.fillGoalsIndependently || '')
      .toString()
      .trim()
      .toLowerCase() === 'true';

    const hasHigherAutocomplete = (goalNum) => {
      return this.goals.some((g) => g.goalNumber > goalNum && g.autocompleteSub && hasSubscription);
    };

    const isAutocompleteReached = (goal) => {
      return hasSubscription && (goal.autocompleteSub || hasHigherAutocomplete(goal.goalNumber));
    };

    // Calculate reached state first so sequential mode can gate later goals.
    this.goals.forEach((goal) => {
      const currentValue = goal.type === 'price' ? cartTotal : cartItemCount;
      goal.reached = currentValue >= goal.threshold || isAutocompleteReached(goal);
    });

    // Sugar Analytics: fire a goal-reached event the first time each goal is
    // met this session — fires on every cart update (drawer open or not), so a
    // threshold crossed before the cart is opened still counts. Deduped per
    // goal via sessionStorage.
    const sugarEntity = this.getAttribute('data-sugar-entity');
    if (sugarEntity) {
      this.goals.forEach((goal) => {
        if (!goal.reached) return;
        let firstTime = true;
        try {
          const key = '_sugar_cprog_' + sugarEntity + '_' + goal.goalNumber;
          if (sessionStorage.getItem(key)) firstTime = false;
          else sessionStorage.setItem(key, '1');
        } catch (e) {}
        if (firstTime) this.sugarTrack('convert', 'unlocked:' + goal.goalNumber, goal.label);
      });
    }

    // Calculate progress for each goal
    this.goals.forEach((goal, index) => {
      const currentValue = goal.type === 'price' ? cartTotal : cartItemCount;
      const goalAutocompleteReached = isAutocompleteReached(goal);
      let progress = 0;

      if (goalAutocompleteReached) {
        progress = 100;
      } else if (index === 0) {
        progress = goal.threshold > 0 ? (currentValue / goal.threshold) * 100 : 100;
      } else if (fillGoalsIndependently) {
        progress = goal.threshold > 0 ? (currentValue / goal.threshold) * 100 : 100;
      } else {
        const previousGoal = this.goals[index - 1];
        if (!previousGoal.reached) {
          progress = 0;
        } else {
          const segmentStart = goal.type === previousGoal.type ? previousGoal.threshold : 0;
          if (currentValue <= segmentStart) {
            progress = 0;
          } else if (currentValue >= goal.threshold) {
            progress = 100;
          } else {
            const segmentRange = goal.threshold - segmentStart;
            progress = segmentRange > 0
              ? ((currentValue - segmentStart) / segmentRange) * 100
              : 100;
          }
        }
      }

      if (!Number.isFinite(progress)) progress = 0;
      progress = Math.max(0, Math.min(progress, 100));

      // Update progress bar UI
      const bar = this.querySelector(`.sugar-cart-progress__bar[data-goal="${index + 1}"], [data-goal="${index + 1}"]`);
      if (bar) {
        bar.style.width = `${progress}%`;
      }

      // Update milestone state
      const milestone = this.querySelector(`.sugar-cart-progress__milestone[data-goal="${index + 1}"]`);
      if (milestone) {
        milestone.classList.toggle('sugar-cart-progress__milestone--reached', goal.reached);
      }
    });

    // Handle auto-add/remove products
    this.handleAutoAddProducts(cartData, source);
  }

  // Qualifying totals exclude the goal gifts themselves. Both final and
  // original totals are kept so remove checks stay stable when cart-level
  // discounts re-allocate after a gift is added.
  qualifyingTotals(cartData) {
    let adjustedFinalTotal = 0;
    let adjustedOriginalTotal = 0;
    let adjustedItemCount = 0;

    cartData.items.forEach((item) => {
      const properties = item?.properties || {};
      const isProgressAutoAdded = Object.prototype.hasOwnProperty.call(
        properties,
        this.autoAddProperty
      );
      if (isProgressAutoAdded) return;

      adjustedFinalTotal += parseInt(item.final_line_price, 10) || 0;
      adjustedOriginalTotal += parseInt(item.original_line_price, 10) || 0;
      adjustedItemCount += parseInt(item.quantity, 10) || 0;
    });

    return {
      hasSubscription: cartData.items.some((item) => item.selling_plan_allocation),
      adjustedFinalTotal,
      adjustedOriginalTotal,
      adjustedItemCount,
    };
  }

  goalStatus(goal, cartData, totals) {
    const autoAddedItem = cartData.items.find((item) =>
      item.variant_id === goal.variantId &&
      item.properties &&
      item.properties[this.autoAddProperty] === String(goal.goalNumber)
    );

    const regularItem = cartData.items.find((item) =>
      item.variant_id === goal.variantId &&
      (!item.properties || !item.properties[this.autoAddProperty])
    );

    // Line totals carry line discounts only, never order discounts, so the gift is judged on
    // the subtotal before order discounts whichever basis the bar shows.
    const addCurrent = goal.type === 'price' ? totals.adjustedFinalTotal : totals.adjustedItemCount;
    const keepCurrent = goal.type === 'price'
      ? Math.max(totals.adjustedFinalTotal, totals.adjustedOriginalTotal)
      : totals.adjustedItemCount;

    // When the auto gift is already in cart, use the stable "keep" value to avoid
    // oscillation from discount re-allocation triggered by the gift line itself.
    const goalCurrent = autoAddedItem ? keepCurrent : addCurrent;
    const hasHigherAutocomplete = this.goals.some(
      (g) => g.goalNumber > goal.goalNumber && g.autocompleteSub && totals.hasSubscription
    );
    const isReached = goalCurrent >= goal.target ||
      (totals.hasSubscription && (goal.autocompleteSub || hasHigherAutocomplete));

    return { autoAddedItem, regularItem, isReached };
  }

  // Keys of goal gifts whose goal is no longer met. Pure: the items component
  // folds them into its own cart update so the gift leaves in one request.
  getGiftKeysToRemove(cartData) {
    if (!cartData || !Array.isArray(cartData.items)) return [];
    const goalsWithProducts = this.goals.filter((g) => g.variantId);
    if (goalsWithProducts.length === 0) return [];

    const totals = this.qualifyingTotals(cartData);
    const keys = [];
    goalsWithProducts.forEach((goal) => {
      const { autoAddedItem, isReached } = this.goalStatus(goal, cartData, totals);
      if (!isReached && autoAddedItem && autoAddedItem.key) keys.push(autoAddedItem.key);
    });
    return keys;
  }

  async handleAutoAddProducts(cartData, source) {
    // Skip if this update came from our own add/remove operations
    if (source === 'sugar-cart-progress') return;

    // Check if any goals have auto-add products configured
    const goalsWithProducts = this.goals.filter(g => g.variantId);
    if (goalsWithProducts.length === 0 || this.processing) return;

    this.processing = true;

    // One bar at a time: the drawer and the cart page can both carry a bar for the same
    // gift, and each must see the cart as the previous one left it.
    const previous = SugarCartProgress.autoAddQueue || Promise.resolve();
    let release;
    SugarCartProgress.autoAddQueue = new Promise((resolve) => { release = resolve; });
    try {
      await previous;
      const latest = window.SugarCart.peek();
      if (window.SugarCart.isCart(latest)) cartData = latest;
      const totals = this.qualifyingTotals(cartData);

      for (const goal of goalsWithProducts) {
        const { autoAddedItem, regularItem, isReached } = this.goalStatus(goal, cartData, totals);

        if (isReached && !autoAddedItem && !regularItem) {
          await this.addProductToCart(goal.variantId, goal.goalNumber);
        } else if (!isReached && autoAddedItem) {
          await this.removeProductFromCart(autoAddedItem.key);
        }
      }
    } catch (e) {
      console.error('Sugar Cart Progress auto-add error:', e);
    } finally {
      this.processing = false;
      release();
    }
  }

  async addProductToCart(variantId, goalNumber) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1,
          properties: {
            [this.autoAddProperty]: String(goalNumber)
          }
        }],
        sections: sugarCartSectionIds(),
        sections_url: window.location.pathname,
      })
    });

    if (response.ok) {
      const result = await response.json();
      const cartData = await this.fetchCart(true);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sugar-cart-progress', cartData, sections: result.sections });
    }
  }

  async removeProductFromCart(lineItemKey) {
    const cartData = await window.SugarCart.change({
      id: lineItemKey,
      quantity: 0,
      sections: sugarCartSectionIds(),
      sections_url: window.location.pathname,
    });

    if (window.SugarCart.isCart(cartData)) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sugar-cart-progress', cartData, sections: cartData.sections });
    }
  }

  fetchCart(fresh = false) {
    return window.SugarCart.get({ fresh });
  }
}

customElements.define('sugar-cart-progress', SugarCartProgress);

// ==========================================================================
// Cart Media: delegated handlers so click-to-add and video pause survive the
// drawer's re-renders.
// ==========================================================================
(function initSugarCartMedia() {
  let mediaBusy = false;

  async function syncCartMediaVisibility(cartData) {
    const blocks = document.querySelectorAll('.sugar-cart-media[data-product-id]');
    if (!blocks.length) return;
    try {
      const cart = window.SugarCart.isCart(cartData) ? cartData : await window.SugarCart.get({ fresh: true });
      const cartProductIds = cart.items.map((item) => item.product_id);
      blocks.forEach((block) => {
        const productId = parseInt(block.dataset.productId, 10);
        const hideWhenInCart = block.dataset.hideWhenInCart === 'true';
        if (hideWhenInCart && cartProductIds.includes(productId)) {
          block.setAttribute('data-hidden', 'true');
        } else {
          block.removeAttribute('data-hidden');
        }
      });
    } catch (error) {
      console.error('Cart Media: visibility sync error', error);
    }
  }

  document.addEventListener('click', async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const wrapper = target.closest('.sugar-cart-media__wrapper');
    if (!wrapper) return;
    const block = wrapper.closest('.sugar-cart-media');
    if (!block) return;

    const variantId = parseInt(block.dataset.variantId, 10);
    if (Number.isFinite(variantId)) {
      event.preventDefault();
      if (mediaBusy) return;
      mediaBusy = true;
      wrapper.setAttribute('data-loading', 'true');
      try {
        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: sugarCartSectionIds(),
            sections_url: window.location.pathname,
          })
        });
        const addResult = await addResponse.json();
        if (addResult.status) {
          console.error('Cart Media: add to cart error', addResult.description);
          return;
        }
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          const cartData = await window.SugarCart.get({ fresh: true });
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-media', cartData, sections: addResult.sections });
        }
        document.dispatchEvent(new CustomEvent('cart:update', { detail: addResult }));
      } catch (error) {
        console.error('Cart Media: add to cart error', error);
      } finally {
        mediaBusy = false;
        wrapper.removeAttribute('data-loading');
      }
      return;
    }

    // No linked product: clicking a video toggles play/pause.
    const video = wrapper.querySelector('.sugar-cart-media__video');
    if (video && typeof video.play === 'function') {
      if (video.paused) video.play();
      else video.pause();
    }
  });

  const syncFromEvent = (cart) => {
    if (window.SugarCart.isCart(cart)) return syncCartMediaVisibility(cart);
    return window.SugarCart.get({ fresh: true }).then(syncCartMediaVisibility).catch(() => {});
  };
  document.addEventListener('cart:update', (event) => {
    const detail = event && event.detail;
    syncFromEvent(detail && (window.SugarCart.isCart(detail.cart) ? detail.cart : detail));
  });
  if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => syncFromEvent(event && event.cartData));
  }
})();
